import type { PlannedSet } from './schemas/plan.js';
import type { SlotDraft } from './programEdit.js';
import { amrapOf, buildIntensity, isCellAbsent, kindAt, parseRepsToken } from './programEditBuild.js';
import { setCountAt } from './programEditRoundSets.js';
import { buildWorkForCell } from './programEditTimeAxis.js';
import { cellRepsRange, type RepsRange } from './programRepsRange.js';
import { effortLabel } from './effortScale.js';
import { formatWeight, type WeightUnitLabels } from './units.js';
import type { UnitsPreference } from './schemas/profile.js';

// Pure formatting of a prescription line: "N × reps @ load". One owner for
// the Plan preview (PlannedSet) and the program editor (Draft cells, #336),
// so the two can never spell the same set two ways. No React, no i18n, no
// catalog lookups: the words a line needs come in through `labels`. Jest-locked
// in previewFormat.test.ts.

const formatNum = (n: number): string => String(Math.round(n * 100) / 100);
/** A bare number as every Draft line spells it (#336 slice 6: the grid's cells). */
export const formatDraftNumber = formatNum;
const pctText = (n: number): string => `${formatNum(n)}%`;

// The ONE spelling of a load on each axis: "80 kg", "75%", "RPE 8". Both the
// Plan label and the Draft lines below come through here.
type LoadAxis = 'absolute' | 'percent-of-max' | 'rpe' | 'rir';
const loadPiece = (
  axis: LoadAxis,
  n: number,
  unit: UnitsPreference,
  locale: string,
  labels: WeightUnitLabels,
): string =>
  axis === 'absolute'
    ? formatWeight(n, unit, locale, labels)
    : axis === 'percent-of-max'
      ? pctText(n)
      : effortLabel(n, axis);

// A low…high range on one axis, the unit said once: "80…100 kg", "70…80%",
// "RPE 7…8". Empty unit words give formatWeight's bare number for the low end.
const NO_UNIT: WeightUnitLabels = { kg: '', lb: '' };
const loadRange = (
  axis: LoadAxis,
  lo: number,
  hi: number,
  unit: UnitsPreference,
  locale: string,
  labels: WeightUnitLabels,
): string => {
  if (lo === hi) return loadPiece(axis, hi, unit, locale, labels);
  if (axis === 'rpe' || axis === 'rir') {
    return `${loadPiece(axis, lo, unit, locale, labels)}…${formatNum(hi)}`;
  }
  const low =
    axis === 'absolute' ? formatWeight(lo, unit, locale, NO_UNIT).trim() : formatNum(lo);
  return `${low}…${loadPiece(axis, hi, unit, locale, labels)}`;
};

// Label for a resolved Plan set's target. A plan set can carry both a
// weight target and an RPE target (the program resolver keeps RPE on
// targetRpe and leaves the weight empty, but a hand-authored plan may
// set both), so the two join with a middot.
//
// The absolute weight goes through formatWeight, the same formatter the
// session summary and the share card use: it converts out of the stored kg
// and appends the matching unit. It used to print a hand-rolled "N kg", so
// an lb user read a kilo figure labelled kg on a surface where every other
// weight had already been converted.
export const plannedTargetLabel = (
  set: PlannedSet,
  unit: UnitsPreference,
  locale = 'en-US',
  labels: WeightUnitLabels,
): string => {
  const parts: string[] = [];
  const w = set.targetWeight;
  // formatWeight's default precision: 2 dp for kg, 1 for lb.
  if (w.kind === 'absolute')
    parts.push(loadPiece('absolute', w.weightKg, unit, locale, labels));
  else if (w.kind === 'percentage')
    parts.push(loadPiece('percent-of-max', w.pctOfMax, unit, locale, labels));
  // The "RIR 2" spelling itself lives in effortScale.ts, the module that owns
  // the two scales — the recorded-effort label on a history row (LG-49) is
  // built by the same function, so the two surfaces can never drift apart.
  if (typeof set.targetRpe === 'number')
    parts.push(loadPiece('rpe', set.targetRpe, unit, locale, labels));
  if (typeof set.targetRir === 'number')
    parts.push(loadPiece('rir', set.targetRir, unit, locale, labels));
  return parts.join(' · ');
};

/** #336 S3, the ONE spelling of a rep range: "8-12", or with the target the
 *  editor's row shows, "10 (8-12)". The logger shows the bare range beside
 *  its prefilled target. */
// U+2011 (non-breaking hyphen): a range never wraps inside itself, whatever
// the font scale or language. Spoken labels keep their own words.
const NB_HYPHEN = '\u2011';
export const formatRepsRange = (range: RepsRange, reps?: number): string => {
  const span = `${range.min}${NB_HYPHEN}${range.max}`;
  return reps === undefined ? span : `${reps} (${span})`;
};

// `work` replaces the reps text when the set is not bounded by reps: a timed
// bout ("40 s") or a reps cell still empty ("?"). `range`: the rep range
// around `reps` (#336 S3), never with `work` or an AMRAP.
type SetCell = { reps: number; label: string; amrap: boolean; work?: string; range?: RepsRange };

// Compact descriptor for one set: "8 @ 70%", AMRAP floor → "8+ @ 70%",
// AMRAP with no floor → "AMRAP", bodyweight/freestyle (no label) → "8",
// a range → "10 (8-12) @ 70%".
const cellText = ({ reps, label, amrap, work, range }: SetCell): string => {
  const repsText =
    work ?? (amrap ? (reps > 0 ? `${reps}+` : 'AMRAP') : range ? formatRepsRange(range, reps) : `${reps}`);
  return label ? `${repsText} @ ${label}` : repsText;
};

const sameCell = (a: SetCell, b: SetCell): boolean =>
  a.reps === b.reps &&
  a.label === b.label &&
  a.amrap === b.amrap &&
  a.work === b.work &&
  a.range?.min === b.range?.min &&
  a.range?.max === b.range?.max;

// Collapse a plan entry's set list into one line. Identical sets fold to
// "N × 8 @ 70%"; a single set drops the count; mixed sets list out
// individually. The collapse keeps the line readable on a narrow screen
// instead of stacking three identical rows. A rep range reads «10 (8-12)»
// (cellText), and sets with another range do not fold together.
export const formatSetGroup = (cells: SetCell[]): string => {
  if (cells.length === 0) return '';
  const first = cells[0]!;
  const uniform = cells.every((c) => sameCell(c, first));
  if (uniform) {
    return cells.length === 1 ? cellText(first) : `${cells.length} × ${cellText(first)}`;
  }
  return cells.map(cellText).join(' · ');
};

// The program editor's Draft (#336). Its cells are strings in the unit the
// draft was seeded in, so they go through the save path's own parsers
// (buildIntensity, parseRepsToken, buildWorkForCell) and then through the
// formatter above. A line here and a line in the Plan preview are one grammar.

/** The words a Draft line needs besides numbers. */
export type DraftLineLabels = {
  units: WeightUnitLabels;
  /** After a timed bout: "40 s". */
  seconds: string;
  /** The history axis ("beat last time"), which carries no number. */
  history: string;
  /** Spoken lead of a set count that differs by round ("3 to 4 sets of"),
   *  read by formatDraftSlotLine's `spoken` form only. */
  setRange?: (lo: number, hi: number) => string;
  /** The wide grid's effort lens, a row in kilos with a MAX: the load of its
   *  hardest set and that set's % of the MAX («100 kg · ≈92 %»). */
  ofMax?: (load: string, pct: string) => string;
};

export type DraftLineOptions = {
  /** The unit the draft cells are written in: see buildIntensity. */
  cellUnit: UnitsPreference;
  /** The unit the line is read in. */
  displayUnit: UnitsPreference;
  locale: string;
  labels: DraftLineLabels;
};

/** A value nobody has typed yet, or one that does not parse. */
const UNSET = '?';

type DraftRow = SlotDraft['setRows'][number];
type LoadKind = Exclude<DraftRow['intensityKind'], 'none'>;
type Span = { lo: number; hi: number };

const widen = (s: Span | undefined, n: number): Span =>
  s ? { lo: Math.min(s.lo, n), hi: Math.max(s.hi, n) } : { lo: n, hi: n };

const spanText = (s: Span, fmt: (n: number) => string): string =>
  s.lo === s.hi ? fmt(s.lo) : `${fmt(s.lo)}…${fmt(s.hi)}`;

// One cell's load as a number on its own axis, or null when absent/unparsable.
const loadValue = (kind: LoadKind, raw: string, o: DraftLineOptions): number | null => {
  const built = buildIntensity(kind, raw, o.cellUnit);
  if (!('kind' in built)) return null;
  if (built.kind === 'absolute') return built.weightKg;
  if (built.kind === 'percent-of-max') return built.pct;
  if (built.kind === 'rpe' || built.kind === 'rir') return built.target;
  return null;
};

// A load over a span of cells: the history word, "?" when no cell parses, or
// the range.
const loadText = (kind: LoadKind, span: Span | undefined, o: DraftLineOptions): string => {
  if (kind === 'history') return o.labels.history;
  if (!span) return UNSET;
  return loadRange(kind, span.lo, span.hi, o.displayUnit, o.locale, o.labels.units);
};

/** One value written in a Draft cell on `kind`, spelled as the lines spell
 *  it: «82.5 kg», «75%», «RPE 8». The builder's rule text reads it (#336). */
export const formatDraftValue = (kind: LoadKind, value: number, o: DraftLineOptions): string => {
  const v = loadValue(kind, String(value), o);
  return loadText(kind, v === null ? undefined : { lo: v, hi: v }, o);
};

// One Draft cell as the formatter's SetCell.
const draftSetCell = (row: DraftRow, w: number, o: DraftLineOptions): SetCell => {
  const cell = row.cells[w];
  if (!cell) return { reps: 0, label: '', amrap: false, work: UNSET };
  const kind = kindAt(row, w);
  const v = kind === 'none' ? null : loadValue(kind, cell.intensityValue, o);
  const label =
    kind === 'none' ? '' : loadText(kind, v === null ? undefined : { lo: v, hi: v }, o);
  if (row.workKind === 'countdown') {
    const { work } = buildWorkForCell(row.workKind, cell.workDurationSec, undefined);
    const secs = work?.kind === 'countdown' ? String(work.durationSec) : UNSET;
    // No AMRAP on a clock-bounded bout (amrapWithTimeError): never drawn.
    // The reps and the clock together, «5 / 40 s»; no reps typed, the clock alone.
    const reps = parseRepsToken(cell.reps)?.reps;
    const clock = `${secs} ${o.labels.seconds}`;
    return { reps: 0, label, amrap: false, work: reps === undefined ? clock : `${reps} / ${clock}` };
  }
  const reps = parseRepsToken(cell.reps)?.reps;
  const amrap = amrapOf(cell);
  const range = cellRepsRange(row, cell);
  return {
    reps: reps ?? 0,
    label,
    amrap,
    ...(reps === undefined && !amrap ? { work: UNSET } : {}),
    ...(range ? { range } : {}),
  };
};

// The Draft's fold: each run of identical consecutive sets becomes "N × set",
// so "4 @ RPE 8.5 · 4 @ RPE 8.5 · 4 @ RPE 8.5 · 5+ @ RPE 8.5" reads
// "3 × 4 @ RPE 8.5 · 5+ @ RPE 8.5". Kept apart from formatSetGroup: the Plan
// preview lists mixed sets one by one and its tests pin that.
// The spaces inside one set group are non-breaking (U+00A0): a narrow panel
// breaks the line at « · » only, never in the middle of «2 × 5 @ RIR 3».
const NBSP = '\u00A0';
const setRuns = (cells: SetCell[]): string[] => {
  const parts: string[] = [];
  for (let i = 0; i < cells.length; ) {
    let n = 1;
    while (i + n < cells.length && sameCell(cells[i]!, cells[i + n]!)) n += 1;
    const text = cellText(cells[i]!);
    parts.push((n === 1 ? text : `${n} × ${text}`).replace(/ /g, NBSP));
    i += n;
  }
  return parts;
};

/** formatDraftWeekLine's set groups, one string each: «2 × 5 @ RPE 6»,
 *  «2 × 5 @ RPE 7». Null when the exercise does not run in round `w`. */
export const formatDraftWeekParts = (
  slot: SlotDraft,
  w: number,
  o: DraftLineOptions,
): string[] | null =>
  slot.inactiveWeeks[w]
    ? null
    : setRuns(slot.setRows.filter((row) => !isCellAbsent(row, w)).map((row) => draftSetCell(row, w, o)));

/** One round of one exercise: "4 × 5 @ RPE 8", "3 × 40 s", or the sets one by
 *  one when they differ, runs of equal sets folded. Only the sets that round
 *  has (#336 S1). Null when the exercise does not run in round `w`. */
export const formatDraftWeekLine = (
  slot: SlotDraft,
  w: number,
  o: DraftLineOptions,
): string | null => formatDraftWeekParts(slot, w, o)?.join(' · ') ?? null;

/** The schema line of one exercise over every round it runs, read from the
 *  values: "4 × 5 @ RPE 7…8". Reps, seconds, each load axis and the set count
 *  (#336 S1: "3…4 × 5") collapse to their low…high. The reps span is the
 *  targets': a rep range (S3) does not widen it, the round lines show it. "+" only when EVERY round
 *  it runs has an AMRAP: the ladder marks the single rounds. Null when it runs
 *  in no round. `spoken`: the set range in words (labels.setRange), for a
 *  screen reader. */
export const formatDraftSlotLine = (
  slot: SlotDraft,
  cycleWeeks: number,
  o: DraftLineOptions,
  spoken = false,
): string | null => {
  let reps: Span | undefined;
  let secs: Span | undefined;
  let sets: Span | undefined;
  let amrap = true;
  let active = false;
  const loads = new Map<LoadKind, Span | undefined>();
  for (let w = 0; w < cycleWeeks; w += 1) {
    if (slot.inactiveWeeks[w]) continue;
    active = true;
    sets = widen(sets, setCountAt(slot, w));
    let roundAmrap = false;
    for (const row of slot.setRows) {
      const cell = row.cells[w];
      if (!cell || isCellAbsent(row, w)) continue;
      if (row.workKind === 'countdown') {
        const { work } = buildWorkForCell(row.workKind, cell.workDurationSec, undefined);
        if (work?.kind === 'countdown') secs = widen(secs, work.durationSec);
      } else {
        const r = parseRepsToken(cell.reps)?.reps;
        if (r !== undefined) reps = widen(reps, r);
        roundAmrap = roundAmrap || amrapOf(cell);
      }
      const kind = kindAt(row, w);
      if (kind === 'none') continue;
      const v = loadValue(kind, cell.intensityValue, o);
      const prev = loads.get(kind);
      loads.set(kind, v === null ? prev : widen(prev, v));
    }
    amrap = amrap && roundAmrap;
  }
  if (!active) return null;
  const work: string[] = [];
  if (reps) work.push(`${spanText(reps, formatNum)}${amrap ? '+' : ''}`);
  else if (amrap) work.push('AMRAP');
  if (secs) work.push(`${spanText(secs, formatNum)} ${o.labels.seconds}`);
  const workText = work.length > 0 ? work.join(' / ') : UNSET;
  const sayRange = spoken && o.labels.setRange && sets && sets.lo !== sets.hi;
  const head =
    !sets || (sets.lo === 1 && sets.hi === 1)
      ? workText
      : sayRange
        ? `${o.labels.setRange!(sets.lo, sets.hi)} ${workText}`
        : `${spanText(sets, formatNum)} × ${workText}`;
  const loadParts = [...loads].map(([kind, span]) => loadText(kind, span, o));
  return loadParts.length > 0 ? `${head} @ ${loadParts.join(' · ')}` : head;
};
